
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

export async function exportarDashboardPDF({
    elementoId,
    titulo = 'Dashboard',
    nomeHospital = '',
    nomeArquivo = 'dashboard.pdf',
    logoUrl = `${process.env.PUBLIC_URL}/assets/pics/logo_HS.png`,
    tabelaId = null,
    anos = []
}) {
    const elemento = document.getElementById(elementoId)

    if (!elemento) {
        throw new Error('Área do dashboard não encontrada.')
    }

    const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
    })

    const larguraPagina = pdf.internal.pageSize.getWidth()
    const alturaPagina = pdf.internal.pageSize.getHeight()

    const margem = 12
    const larguraUtil = larguraPagina - margem * 2

    const topoConteudo = 42
    const rodapeInicio = alturaPagina - 13
    const espacamentoBlocos = 5

    let numeroPagina = 1
    let posicaoY = topoConteudo
    let paginaInicializada = false

    // =====================================================
    // LOGOTIPO
    // =====================================================

    let logo = null
    let logoProporcao = 1

    try {
        const imagemLogo = new Image()
        imagemLogo.crossOrigin = 'anonymous'

        await new Promise((resolve, reject) => {
            imagemLogo.onload = resolve
            imagemLogo.onerror = reject
            imagemLogo.src = logoUrl
        })

        const canvasLogo = document.createElement('canvas')
        canvasLogo.width = imagemLogo.naturalWidth
        canvasLogo.height = imagemLogo.naturalHeight

        const ctx = canvasLogo.getContext('2d')
        ctx.drawImage(imagemLogo, 0, 0)

        logo = canvasLogo.toDataURL('image/png')

        logoProporcao =
            imagemLogo.naturalWidth / imagemLogo.naturalHeight

    } catch (error) {
        console.warn('Não foi possível carregar o logotipo:', error)
    }

    // =====================================================
    // CABEÇALHO E RODAPÉ
    // =====================================================

    const desenharCabecalho = () => {
        const inicioTexto = logo ? margem + 40 : margem

        if (logo) {
            const larguraLogo = 34
            const alturaLogo = larguraLogo / logoProporcao

            pdf.addImage(
                logo,
                'PNG',
                margem,
                7,
                larguraLogo,
                Math.min(alturaLogo, 20)
            )
        }

        pdf.setFont('helvetica', 'bold')
        pdf.setFontSize(15)
        pdf.setTextColor(35, 80, 105)

        pdf.text(
            titulo,
            inicioTexto,
            16,
            { maxWidth: larguraPagina - inicioTexto - margem }
        )

        if (nomeHospital) {
            pdf.setFont('helvetica', 'bold')
            pdf.setFontSize(10)
            pdf.setTextColor(65, 65, 65)

            pdf.text(
                nomeHospital,
                inicioTexto,
                21,
                { maxWidth: larguraPagina - inicioTexto - margem }
            )
        }

        pdf.setFont('helvetica', 'normal')
        pdf.setFontSize(9)
        pdf.setTextColor(100, 100, 100)

        const periodo =
            Array.isArray(anos) && anos.length
                ? `Período: ${anos.join(', ')}`
                : 'Período: todos os anos'

        pdf.text(periodo, inicioTexto, 27)

        pdf.text(
            `Emitido em: ${new Date().toLocaleDateString('pt-BR')}`,
            larguraPagina - margem,
            27,
            { align: 'right' }
        )

        pdf.setDrawColor(210, 220, 225)
        pdf.line(margem, 33, larguraPagina - margem, 33)

        pdf.setFont('helvetica', 'normal')
        pdf.setFontSize(8)
        pdf.setTextColor(110, 110, 110)

        pdf.text(
            `Página ${numeroPagina}`,
            larguraPagina - margem,
            alturaPagina - 6,
            { align: 'right' }
        )
    }

    // =====================================================
    // CONTROLE DE PÁGINAS
    // =====================================================

    const iniciarPagina = () => {
        if (paginaInicializada) {
            pdf.addPage()
            numeroPagina++
        }

        paginaInicializada = true
        posicaoY = topoConteudo

        desenharCabecalho()
    }

    // =====================================================
    // CAPTURA DO ELEMENTO
    // =====================================================

    const capturarElemento = async elementoCaptura => {
        return html2canvas(elementoCaptura, {
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true,
            logging: false,

            windowWidth: Math.max(
                document.documentElement.clientWidth,
                elementoCaptura.scrollWidth
            ),

            onclone: documentoClonado => {
                // Remove apenas da captura o cabeçalho
                // que já será apresentado no PDF.
                documentoClonado
                    .querySelectorAll('.cabecalho-dashboard-interno')
                    .forEach(cabecalho => {
                        cabecalho.style.display = 'none'
                    })
            }
        })
    }

    // =====================================================
    // INSERÇÃO PAGINADA
    // =====================================================

    const adicionarElemento = async elementoCaptura => {
        if (!elementoCaptura) return

        const canvas = await capturarElemento(elementoCaptura)

        if (!canvas.width || !canvas.height) return

        const proporcao = larguraUtil / canvas.width

        let inicioPixel = 0

        while (inicioPixel < canvas.height) {
            if (!paginaInicializada) {
                iniciarPagina()
            }

            let espacoDisponivel = rodapeInicio - posicaoY

            if (espacoDisponivel <= 1) {
                iniciarPagina()
                espacoDisponivel = rodapeInicio - posicaoY
            }

            const pixelsDisponiveis = Math.max(
                1,
                Math.floor(espacoDisponivel / proporcao)
            )

            const alturaFaixa = Math.min(
                pixelsDisponiveis,
                canvas.height - inicioPixel
            )

            const canvasFaixa = document.createElement('canvas')

            canvasFaixa.width = canvas.width
            canvasFaixa.height = alturaFaixa

            const contexto = canvasFaixa.getContext('2d')

            contexto.drawImage(
                canvas,
                0,
                inicioPixel,
                canvas.width,
                alturaFaixa,
                0,
                0,
                canvas.width,
                alturaFaixa
            )

            const imagemFaixa = canvasFaixa.toDataURL('image/png')
            const alturaFaixaMM = alturaFaixa * proporcao

            pdf.addImage(
                imagemFaixa,
                'PNG',
                margem,
                posicaoY,
                larguraUtil,
                alturaFaixaMM
            )

            posicaoY += alturaFaixaMM
            inicioPixel += alturaFaixa

            if (inicioPixel < canvas.height) {
                iniciarPagina()
            }
        }
    }

    // =====================================================
    // DASHBOARD PRINCIPAL
    // =====================================================

    await adicionarElemento(elemento)

    // =====================================================
    // TABELA COMPLEMENTAR
    // =====================================================

    if (tabelaId) {
        const tabela = document.getElementById(tabelaId)

        if (tabela) {
            if (posicaoY + espacamentoBlocos < rodapeInicio) {
                posicaoY += espacamentoBlocos
            } else {
                iniciarPagina()
            }

            await adicionarElemento(tabela)
        } else {
            console.warn(
                `Tabela com ID "${tabelaId}" não encontrada.`
            )
        }
    }

    pdf.save(nomeArquivo)
}
